import type { DataItem } from '@pn/core/domain/data';
import {
  createIntelReportData,
  type IntelReport,
  type IntelReportData,
} from '@pn/core/domain/intel/intel-report';
import type { IntelDataItem } from '@pn/core/domain/intel/intel-workspace-item';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type IntelReportsState = {
  isFetching: boolean;
  isEditingReport: boolean;
  reports: IntelReport[];
  reportsData: IntelReportData[];
  selectedReportId: string;
  loadedReportIds: IntelReport['id'][];
};

const initialState: IntelReportsState = {
  isFetching: false,
  isEditingReport: false,
  reports: [],
  reportsData: [],
  selectedReportId: '',
  loadedReportIds: [],
};

const createReportsSlice = (sliceName: string) =>
  createSlice({
    name: sliceName,
    initialState,
    reducers: {
      request: (state) => {
        state.isFetching = true;
      },
      receive: (state, { payload }: PayloadAction<IntelReport[]>) => {
        state.isFetching = false;
        state.reports = payload;
      },
      toggleIsEditingReport: (state) => {
        state.isEditingReport = !state.isEditingReport;
      },
      create: (state, params) => {
        const report = params.payload;
        state.reports.push(report);
        state.selectedReportId = report.id;
        state.isEditingReport = false;
      },
      saveSharedReport: (
        state,
        {
          payload,
        }: PayloadAction<{ oldReportId: string; savedReport: IntelReport }>
      ) => {
        state.reports = state.reports.map((report) =>
          report.id === payload.oldReportId ? payload.savedReport : report
        );
      },
      update: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          updatedReport: IntelReport;
        }>
      ) => {
        const report = state.reports.find(
          (report) => report.id === payload.reportId
        );
        if (!report) return;
        state.reports = state.reports.map((report) =>
          report.id === payload.reportId ? payload.updatedReport : report
        );
        state.loadedReportIds = state.loadedReportIds.filter(
          (id) => id !== payload.reportId
        );
      },
      updateEmailSchedule: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          emailFrequency: IntelReport['emailSchedule'];
        }>
      ) => {
        const report = state.reports.find(
          (report) => report.id === payload.reportId
        );
        if (!report) return;
        report.emailSchedule = payload.emailFrequency;
      },
      updateIsPinned: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          isPinned: boolean;
        }>
      ) => {
        const report = state.reports.find(
          (report) => report.id === payload.reportId
        );
        if (!report) return;
        report.isPinned = payload.isPinned;
      },
      remove: (state, { payload }: PayloadAction<IntelReport['id']>) => {
        state.reports = state.reports.filter((report) => report.id !== payload);
      },
      setProduction: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          production: IntelReportData['production'];
        }>
      ) => {
        const reportData = state.reportsData.find(
          (report) => report.reportId === payload.reportId
        );
        if (!reportData) return;
        reportData.production = payload.production;
        reportData.isLoadingProduction = false;
      },
      setYearOverYearData: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          spudsYearOverYear: IntelDataItem[];
          licencesYearOverYear: IntelDataItem[];
        }>
      ) => {
        const reportData = state.reportsData.find(
          (report) => report.reportId === payload.reportId
        );
        if (!reportData) return;
        reportData.spudsYearOverYear = payload.spudsYearOverYear;
        reportData.licencesYearOverYear = payload.licencesYearOverYear;
        reportData.isLoadingYearOverYear = false;
      },
      setMonthlyLicences: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          monthlyLicences: DataItem[];
        }>
      ) => {
        const reportData = state.reportsData.find(
          (report) => report.reportId === payload.reportId
        );
        if (!reportData) return;
        reportData.monthlyLicenses = payload.monthlyLicences;
        reportData.isLoadingMonthlyLicenses = false;
      },
      setMonthlySpuddedLicenses: (
        state,
        {
          payload,
        }: PayloadAction<{
          reportId: IntelReport['id'];
          monthlySpuddedLicenses: DataItem[];
        }>
      ) => {
        const reportData = state.reportsData.find(
          (report) => report.reportId === payload.reportId
        );
        if (!reportData) return;
        reportData.monthlySpuddedLicenses = payload.monthlySpuddedLicenses;
        reportData.isLoadingMonthlySpuddedLicenses = false;
      },
      setIsLoadingProduction: (
        state,
        { payload }: PayloadAction<IntelReport['id']>
      ) => {
        const reportData = state.reportsData.find(
          (report) => report.reportId === payload
        );
        if (!reportData) return;
        reportData.isLoadingProduction = true;
      },
      setIsLoadingWells: (
        state,
        { payload }: PayloadAction<IntelReport['id']>
      ) => {
        const reportData = state.reportsData.find(
          (report) => report.reportId === payload
        );
        if (!reportData) return;
        reportData.isLoadingMonthlyLicenses = true;
        reportData.isLoadingYearOverYear = true;
        reportData.isLoadingMonthlySpuddedLicenses = true;
      },
      markReportAsLoaded: (state, action) => {
        const reportData = createIntelReportData(action.payload);
        state.reportsData.push(reportData);
        state.loadedReportIds.push(action.payload);
      },
      selectReport: (state, action) => {
        state.selectedReportId = action.payload;
      },
      unselectReport: (state) => {
        state.selectedReportId = '';
      },
      error: (state) => {
        state.isFetching = false;
        state.isEditingReport = false;
        state.reports = [];
        state.reportsData = [];
        state.selectedReportId = '';
      },
    },
  });

export const intelReportsSlice = createReportsSlice('intelReports');
